"use client";
import { USBDevice } from "@/app/@types/webUSB";
import isChromium from "@/app/_helpers/chromiumBrowserDetect";
import {
  getPrinters,
  probePrinter,
  sendZPLToPrinter,
} from "@/app/_helpers/printer";
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/app/_firebase/firebase";
import { PrintQueue } from "@/app/@types/printer";
import { removePrintJob } from "@/app/_firebase/printer";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import { Flex, Text } from "@mantine/core";

interface PrinterContextType {
  activePrinter: USBDevice | null;
  setActivePrinter: (printer: USBDevice | null) => void;
  // true = probe succeeded, false = probe or a print failed, null = unknown/checking
  printerHealthy: boolean | null;
  recheckPrinter: () => Promise<void>;
}

export const PrinterContext = createContext<PrinterContextType | undefined>(
  undefined
);

export const usePrinter = () => {
  const context = useContext(PrinterContext);
  if (!context) {
    throw new Error("usePrinter must be used within a PrinterProvider");
  }
  return context;
};

const isSameDevice = (a: USBDevice, b: USBDevice) =>
  a.vendorId === b.vendorId &&
  a.productId === b.productId &&
  a.serialNumber === b.serialNumber;

export const PrinterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activePrinter, setActivePrinterState] = useState<USBDevice | null>(
    null
  );
  const [printerHealthy, setPrinterHealthy] = useState<boolean | null>(null);
  const [isValidBrowser, setIsValidBrowser] = useState(false);
  const user = useUserContext();
  const setAlert = useSetAlert();

  // The USB event listeners are registered once, so they read the current
  // printer through a ref instead of re-subscribing on every change.
  const printerRef = useRef<USBDevice | null>(null);
  printerRef.current = activePrinter;

  // Enumerating isn't enough — probe (open + claim) so the status we show
  // reflects whether we can actually print, not just that the device exists.
  const setActivePrinter = useCallback(async (printer: USBDevice | null) => {
    setActivePrinterState(printer);
    setPrinterHealthy(null);
    if (printer) {
      setPrinterHealthy(await probePrinter(printer));
    }
  }, []);

  const recheckPrinter = useCallback(async () => {
    const printer = printerRef.current;
    if (!printer) return;
    setPrinterHealthy(null);
    setPrinterHealthy(await probePrinter(printer));
  }, []);

  // Initialize printer connection
  useEffect(() => {
    const initializePrinters = async () => {
      const printers = await getPrinters();
      // to test the printer connection
      // setActivePrinter({} as USBDevice);
      if (printers.length > 0) {
        setActivePrinter(printers[0]);
      }
    };

    if (isChromium()) {
      setIsValidBrowser(true);
      initializePrinters();
    } else {
      setIsValidBrowser(false);
    }
  }, [setActivePrinter]);

  // Track unplug/replug (and power cycles) so the connection status stays
  // truthful after page load. These events only fire for devices the site
  // already has permission for.
  useEffect(() => {
    // @ts-ignore
    const usb = typeof navigator !== "undefined" ? navigator.usb : undefined;
    if (!isChromium() || !usb) return;

    const handleConnect = (event: { device: USBDevice }) => {
      if (
        !printerRef.current ||
        isSameDevice(event.device, printerRef.current)
      ) {
        setActivePrinter(event.device);
      }
    };
    const handleDisconnect = (event: { device: USBDevice }) => {
      if (
        printerRef.current &&
        isSameDevice(event.device, printerRef.current)
      ) {
        setActivePrinterState(null);
        setPrinterHealthy(null);
      }
    };

    usb.addEventListener("connect", handleConnect);
    usb.addEventListener("disconnect", handleDisconnect);
    return () => {
      usb.removeEventListener("connect", handleConnect);
      usb.removeEventListener("disconnect", handleDisconnect);
    };
  }, [setActivePrinter]);

  // Listen to print queue and auto-print when printer is connected
  useEffect(() => {
    if (!activePrinter || !user?.uid) return;

    const printQueueRef = doc(db, `users/${user.uid}/metadata/printQueue`);

    const unsubscribe = onSnapshot(printQueueRef, async (snapshot) => {
      if (!snapshot.exists()) return;

      const queueData = snapshot.data() as PrintQueue;

      if (queueData.all.length === 0) return;

      // Process only the first item (FIFO)
      const zpl = queueData.all[0];

      try {
        // Send ZPL to printer
        await sendZPLToPrinter(activePrinter, zpl);
        setPrinterHealthy(true);
        console.log("Print successful");
      } catch (error) {
        console.error("Print failed:", error);
        setPrinterHealthy(false);
        setAlert({
          type: "error",
          title: "Tag failed to print",
          content: (
            <Flex direction="column" gap="xs">
              <Text size="sm">
                The tag printer did not accept the print job. Check it is
                turned on, has labels loaded, and the print head is closed,
                then try again.
              </Text>
              <Text size="sm">
                If printing keeps failing, please contact support.
              </Text>
            </Flex>
          ),
        });
      } finally {
        // Remove from queue regardless of success/failure
        // This triggers a new snapshot and processes the next item
        await removePrintJob(user.uid, zpl);
      }
    });

    return () => unsubscribe();
  }, [activePrinter, user?.uid, setAlert]);

  const value = {
    activePrinter,
    setActivePrinter,
    printerHealthy,
    recheckPrinter,
  };

  return (
    <PrinterContext.Provider value={value}>{children}</PrinterContext.Provider>
  );
};
